import { Text, Flex, Card, Spinner, Avatar } from '@radix-ui/themes'
import { useState, useEffect } from 'react'

interface Restaurant {
  id: string
  name: string
  address: string
  coordinates: {
    latitude: number
    longitude: number
  }
}

interface Record {
  restaurant: Restaurant
  content: string
  images: string[]
  timestamp: number
  userEmail: string
  recordKey: string
  imageKeys: string[]
  photoTakenAt?: string
}

export const FeedPage = () => {
  const [records, setRecords] = useState<Record[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [imageData, setImageData] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    fetchRecords()
  }, [])

  const fetchRecords = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/records')
      if (!response.ok) {
        throw new Error('Failed to fetch records')
      }

      const data = await response.json()
      const sortedRecords = data.sort((a: Record, b: Record) => b.timestamp - a.timestamp)
      setRecords(sortedRecords)

      // 각 레코드의 이미지 로드
      for (const record of sortedRecords) {
        // imageKeys가 있으면 KV에서 가져오기
        if (record.imageKeys && record.imageKeys.length > 0) {
          for (const imageKey of record.imageKeys) {
            await fetchImage(imageKey)
          }
        } else if (record.images && record.images.length > 0) {
          // imageKeys가 없으면 base64 이미지를 바로 사용
          const tempImageData: { [key: string]: string } = {}
          record.images.forEach((image: string, index: number) => {
            const tempKey = `temp-${record.timestamp}-${index}`
            tempImageData[tempKey] = image
          })
          setImageData((previous) => ({ ...previous, ...tempImageData }))
        }
      }
    } catch (error_) {
      console.error('Failed to fetch records:', error_)
      setError('기록을 불러오는데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const fetchImage = async (imageKey: string) => {
    try {
      const response = await fetch(`/api/images?key=${imageKey}`)
      if (response.ok) {
        const data = await response.json()
        setImageData((previous) => ({ ...previous, [imageKey]: data.image }))
      }
    } catch (error_) {
      console.error('Failed to fetch image:', error_)
    }
  }

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return '방금 전'
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}분 전`
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}시간 전`
    } else if (diffInSeconds < 604800) {
      return `${Math.floor(diffInSeconds / 86400)}일 전`
    } else {
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
  }

  const getUserInitial = (email: string) => {
    return email.charAt(0).toUpperCase()
  }

  if (loading) {
    return (
      <Flex direction="column" gap="4" align="center" style={{ paddingTop: '40px' }}>
        <Spinner size="3" />
        <Text color="gray">기록을 불러오는 중...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex direction="column" gap="4" align="center" style={{ paddingTop: '40px' }}>
        <Text color="red" size="4">
          {error}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="center">
        <Text size="5" weight="bold">
          음식 피드
        </Text>
        <Text size="2" color="gray">
          {records.length}개의 기록
        </Text>
      </Flex>

      {records.length === 0 ? (
        <Card>
          <Flex direction="column" gap="2" align="center" style={{ padding: '40px 20px' }}>
            <Text size="4" color="gray">
              아직 기록이 없습니다
            </Text>
            <Text size="2" color="gray">
              지도에서 음식점을 선택하고 기록해보세요!
            </Text>
          </Flex>
        </Card>
      ) : (
        <Flex direction="column" gap="3">
          {records.map((record) => (
            <Card key={record.recordKey}>
              <Flex direction="column" gap="3">
                {/* 사용자 정보 헤더 */}
                <Flex align="center" gap="3">
                  <Avatar
                    size="2"
                    fallback={getUserInitial(record.userEmail)}
                    color="blue"
                  />
                  <Flex direction="column" gap="0">
                    <Text size="2" weight="bold">
                      {record.userEmail.split('@')[0]}
                    </Text>
                    <Text size="1" color="gray">
                      {formatDate(record.timestamp)}
                    </Text>
                  </Flex>
                </Flex>

                {/* 음식점 정보 */}
                <Flex
                  direction="column"
                  gap="1"
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--gray-2)',
                    borderRadius: '8px',
                  }}
                >
                  <Text size="3" weight="bold">
                    {record.restaurant.name}
                  </Text>
                  <Text size="1" color="gray">
                    {record.restaurant.address}
                  </Text>
                  {record.photoTakenAt && (
                    <Text size="1" color="blue">
                      📸 {record.photoTakenAt.replace(':', '년 ').replace(':', '월 ').replace(' ', '일 ')}
                    </Text>
                  )}
                </Flex>

                {/* 이미지 그리드 */}
                {((record.imageKeys && record.imageKeys.length > 0) || (record.images && record.images.length > 0)) && (
                  <Flex
                    gap="2"
                    wrap="wrap"
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        (record.imageKeys?.length || record.images?.length || 1) === 1
                          ? '1fr'
                          : (record.imageKeys?.length || record.images?.length || 2) === 2
                          ? 'repeat(2, 1fr)'
                          : 'repeat(3, 1fr)',
                      gap: '8px',
                    }}
                  >
                    {(record.imageKeys || []).length > 0
                      ? record.imageKeys.map((imageKey, index) => (
                          <div
                            key={imageKey}
                            style={{
                              position: 'relative',
                              width: '100%',
                              paddingBottom: '100%',
                              backgroundColor: 'var(--gray-3)',
                              borderRadius: '8px',
                              overflow: 'hidden',
                            }}
                          >
                            {imageData[imageKey] ? (
                              <img
                                src={imageData[imageKey]}
                                alt={`${record.restaurant.name} - ${index + 1}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <Flex
                                align="center"
                                justify="center"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                <Spinner size="2" />
                              </Flex>
                            )}
                          </div>
                        ))
                      : record.images?.map((image, index) => {
                          const tempKey = `temp-${record.timestamp}-${index}`
                          return (
                            <div
                              key={tempKey}
                              style={{
                                position: 'relative',
                                width: '100%',
                                paddingBottom: '100%',
                                backgroundColor: 'var(--gray-3)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                              }}
                            >
                              <img
                                src={imageData[tempKey] || image}
                                alt={`${record.restaurant.name} - ${index + 1}`}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                          )
                        })}
                  </Flex>
                )}

                {/* 메모 내용 */}
                <Text size="2" style={{ whiteSpace: 'pre-wrap' }}>
                  {record.content}
                </Text>
              </Flex>
            </Card>
          ))}
        </Flex>
      )}
    </Flex>
  )
}